import classNames from 'clsx';
import React, { cloneElement, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { noop } from 'reactApp/utils/helpers';

import styles from './Hint.css';

const isMobile = EnvironmentSelectors.isMobile() || EnvironmentSelectors.isIpadOs();
const isPhone = EnvironmentSelectors.isPhone();

const PADDING = 10;

export const Hint = ({
    children,
    text,
    theme = '',
    showDelay = false,
}: {
    children: ReactElement;
    text: string | ReactElement;
    theme?: '' | 'dark';
    showDelay?: boolean;
}) => {
    const hintRef = useRef<HTMLDivElement | null>(null);
    const [show, setShow] = useState(false);
    const [position, setPoison] = useState({ left: 0, top: 0 });
    const timerRef = useRef<number | null>(null);

    const clearTimer = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
        }
        timerRef.current = null;
    };

    const showHint = useCallback(
        (e) => {
            if (!text) {
                return;
            }

            setPoison({
                left: e.pageX,
                top: e.pageY,
            });

            const show = () => setShow(true);
            if (showDelay) {
                clearTimer();
                timerRef.current = window.setTimeout(show, 1000);
            } else {
                show();
            }
        },
        [text, showDelay, clearTimer]
    );

    const hideHint = useCallback(() => {
        setShow(false);
        clearTimer();
    }, []);

    useEffect(() => {
        window.addEventListener('fullscreenchange', hideHint);

        return (): void => {
            window.removeEventListener('fullscreenchange', hideHint);
            clearTimer();
        };
    }, []);

    const onClick = useCallback(
        (e) => {
            setShow(false);
            if (children.props.onClick) {
                children.props.onClick(e);
            }
            clearTimer();
        },
        [children]
    );

    useEffect(() => {
        if (hintRef.current) {
            const hintRect = hintRef.current.getBoundingClientRect();
            const windowWidth = Math.min(window.innerWidth, window.document.body.clientWidth);
            let { left } = position;
            const { top } = position;

            if (hintRect.width + left + PADDING >= windowWidth) {
                left = windowWidth - hintRect.width - PADDING;
            } else {
                left += PADDING;
            }

            hintRef.current.style.left = `${left}px`;
            hintRef.current.style.top = `${top + PADDING}px`;
            hintRef.current.style.opacity = '1';
        }
    }, [show]);

    return (
        <>
            {show && (
                <Portal>
                    <div ref={hintRef} className={classNames({ [styles.root]: true, [styles[`root_${theme}`]]: !!theme })}>
                        {text}
                    </div>
                </Portal>
            )}
            {cloneElement(children, {
                onMouseEnter: isMobile ? noop : showHint,
                onMouseLeave: isMobile ? noop : hideHint,
                onClick: isPhone ? noop : onClick,
            })}
        </>
    );
};
